import { parseDateString } from '@/utils/date-utils';

export enum CreatePriceMode {
  Create = 'Create',
  Approve = 'Approve',
}

export enum CreatePriceQueryParams {
  Mode = 'mode',
  ValidFrom = 'validFrom',
  PriceUnitCategoryId = 'priceUnitCategoryId',
  PriceUnits = 'priceUnits',
  Products = 'products',
  Themes = 'themes',
  Groups = 'groups',
  FirstHalfYear = 'firstHalfYear',
  SecondHalfYear = 'secondHalfYear',
  CarryOvers = 'carryOvers',
  Outgoing = 'outgoing',
  Approved = 'approved',
  Preliminary = 'preliminary',
  OnlyZeroPrices = 'onlyZeroPrices',
  FromSession = 'fromSession',
  Published = 'published',
}

export interface CreatePriceModalFilters {
  validFromDate: Date;
  priceUnitCategoryId: number | null;
  priceUnitIds: number[];
  productIds: number[];
  themeIds: string[];
  groupIds: string[];
  productStatus: {
    firstHalfYear: boolean;
    secondHalfYear: boolean;
    carryOvers: boolean;
    outgoing: boolean;
  };
  priceStatus: {
    approved: boolean;
    preliminary: boolean;
  };
  onlyZeroPrices: boolean;
}

export interface DecodedCreatePriceParams {
  mode: CreatePriceMode;
  filters: CreatePriceModalFilters;
  fromSession: boolean;
  published: boolean;
}

const readNumberParams = (
  params: URLSearchParams,
  key: CreatePriceQueryParams,
): number[] =>
  params
    .getAll(key)
    .map(Number)
    .filter((value) => Number.isFinite(value));

export function encodeCreatePriceParams(
  filters: CreatePriceModalFilters,
  mode: CreatePriceMode = CreatePriceMode.Create,
  options?: { fromSession?: boolean; published?: boolean },
): URLSearchParams {
  const params = new URLSearchParams();

  params.set(CreatePriceQueryParams.Mode, mode);

  const year = filters.validFromDate.getFullYear();
  const month = String(filters.validFromDate.getMonth() + 1).padStart(2, '0');
  const day = String(filters.validFromDate.getDate()).padStart(2, '0');
  params.set(CreatePriceQueryParams.ValidFrom, `${year}-${month}-${day}`);

  for (const id of filters.priceUnitIds) {
    params.append(CreatePriceQueryParams.PriceUnits, String(id));
  }
  if (filters.priceUnitCategoryId != null) {
    params.set(
      CreatePriceQueryParams.PriceUnitCategoryId,
      String(filters.priceUnitCategoryId),
    );
  }
  for (const id of filters.productIds) {
    params.append(CreatePriceQueryParams.Products, String(id));
  }
  for (const id of filters.themeIds) {
    params.append(CreatePriceQueryParams.Themes, id);
  }
  for (const id of filters.groupIds) {
    params.append(CreatePriceQueryParams.Groups, id);
  }

  if (filters.productStatus.firstHalfYear) {
    params.set(CreatePriceQueryParams.FirstHalfYear, 'true');
  }
  if (filters.productStatus.secondHalfYear) {
    params.set(CreatePriceQueryParams.SecondHalfYear, 'true');
  }
  if (filters.productStatus.carryOvers) {
    params.set(CreatePriceQueryParams.CarryOvers, 'true');
  }
  if (filters.productStatus.outgoing) {
    params.set(CreatePriceQueryParams.Outgoing, 'true');
  }

  if (filters.priceStatus.approved) {
    params.set(CreatePriceQueryParams.Approved, 'true');
  }
  if (filters.priceStatus.preliminary) {
    params.set(CreatePriceQueryParams.Preliminary, 'true');
  }

  if (filters.onlyZeroPrices) {
    params.set(CreatePriceQueryParams.OnlyZeroPrices, 'true');
  }

  if (options?.fromSession) {
    params.set(CreatePriceQueryParams.FromSession, 'true');
  }
  if (options?.published) {
    params.set(CreatePriceQueryParams.Published, 'true');
  }

  return params;
}

export function decodeCreatePriceParams(
  params: URLSearchParams,
): DecodedCreatePriceParams {
  const mode: CreatePriceMode =
    params.get(CreatePriceQueryParams.Mode)?.toLowerCase() === 'approve'
      ? CreatePriceMode.Approve
      : CreatePriceMode.Create;

  const validFromStr = params.get(CreatePriceQueryParams.ValidFrom) ?? '';
  const validFromDate = validFromStr
    ? parseDateString(validFromStr)
    : new Date();

  return {
    mode,
    fromSession: params.get(CreatePriceQueryParams.FromSession) === 'true',
    published: params.get(CreatePriceQueryParams.Published) === 'true',
    filters: {
      validFromDate,
      priceUnitCategoryId: params.has(
        CreatePriceQueryParams.PriceUnitCategoryId,
      )
        ? Number(params.get(CreatePriceQueryParams.PriceUnitCategoryId))
        : null,
      priceUnitIds: readNumberParams(params, CreatePriceQueryParams.PriceUnits),
      productIds: readNumberParams(params, CreatePriceQueryParams.Products),
      themeIds: params.getAll(CreatePriceQueryParams.Themes),
      groupIds: params.getAll(CreatePriceQueryParams.Groups),
      productStatus: {
        firstHalfYear:
          params.get(CreatePriceQueryParams.FirstHalfYear) === 'true',
        secondHalfYear:
          params.get(CreatePriceQueryParams.SecondHalfYear) === 'true',
        carryOvers: params.get(CreatePriceQueryParams.CarryOvers) === 'true',
        outgoing: params.get(CreatePriceQueryParams.Outgoing) === 'true',
      },
      priceStatus: {
        approved: params.get(CreatePriceQueryParams.Approved) === 'true',
        preliminary: params.get(CreatePriceQueryParams.Preliminary) === 'true',
      },
      onlyZeroPrices:
        params.get(CreatePriceQueryParams.OnlyZeroPrices) === 'true',
    },
  };
}
